// URL / API: feature_filter_groups JSON array.

import Utils from "../../Utils";

export const PARAM_GROUPS = "feature_filter_groups";

export const ENABLED = {
  ON: "1",
  OFF: "0",
};

export const MATCH = {
  ALL: "all",
  ANY: "any",
};

export function emptyGroup() {
  return {
    features: [],
    enabled: ENABLED.ON,
    match: MATCH.ALL,
  };
}

export function normalizeGroup(group = {}) {
  const features = Array.isArray(group.features)
    ? group.features.filter(Boolean)
    : [];

  const enabled =
    String(group.enabled) === ENABLED.OFF ? ENABLED.OFF : ENABLED.ON;
  const match = group.match === MATCH.ANY ? MATCH.ANY : MATCH.ALL;

  return { features, enabled, match };
}

function tryParseGroupsJson(raw) {
  const attempts = [raw];

  try {
    attempts.push(decodeURIComponent(raw));
  } catch (e) {}

  for (let i = 0; i < attempts.length; i++) {
    try {
      const parsed = JSON.parse(attempts[i]);
      if (Array.isArray(parsed) && parsed.length) {
        return parsed.map(normalizeGroup);
      }
    } catch (e) {}
  }

  return [];
}

export function parseFilterFromParams(params = {}) {
  const groups = tryParseGroupsJson(params[PARAM_GROUPS]);
  return { groups };
}

export function validGroups(groups = []) {
  return groups.filter((group) => group.features && group.features.length);
}

function groupsPayload(groups = []) {
  return validGroups(groups).map((group) => ({
    features: group.features,
    enabled: group.enabled === ENABLED.OFF ? ENABLED.OFF : ENABLED.ON,
    match: group.match === MATCH.ANY ? MATCH.ANY : MATCH.ALL,
  }));
}

export function groupsToUrl(groups = []) {
  const payload = groupsPayload(groups);
  return payload.length ? JSON.stringify(payload) : undefined;
}

export function buildFeatureFilterApiParams(filter = {}) {
  const serialized = groupsToUrl(filter.groups || []);

  if (!serialized) {
    return {};
  }

  return { [PARAM_GROUPS]: serialized };
}

export function filterKey(featureFilter = {}) {
  return JSON.stringify(featureFilter.groups || []);
}

export function hasActiveFeatureFilter(featureFilter = {}) {
  return validGroups(featureFilter.groups || []).length > 0;
}

export function buildFeatureFilterOptions(featuresResponse = {}) {
  const flat = Utils.flatten(featuresResponse.features || {});

  return Object.keys(flat)
    .filter((key) => key.startsWith("feature_") || key.startsWith("new_"))
    .sort((a, b) => a.localeCompare(b))
    .map((key) => ({
      value: key,
      label: Utils.capitalize(
        key.replace("feature_", "").split("_").join(" "),
        true,
      ),
    }));
}

export function editorGroups(featureFilter = {}) {
  const fromUrl = featureFilter.groups || [];

  if (fromUrl.length) {
    return fromUrl.map(normalizeGroup);
  }

  return [emptyGroup()];
}

// GeckoCardTable / GeckoPagination expect total_items + total_pages (not Laravel total/last_page)
export function paginationFromAccountsResponse(data = {}) {
    const rows = data.data || [];
    const parsedTotal = Number(data.total);
    const totalItems = Number.isFinite(parsedTotal) ? parsedTotal : rows.length;
    const perPage = Number(data.per_page) || rows.length || 1;
    const currentPage = Number(data.current_page) || 1;
    let totalPages = Number(data.last_page);

    if (!totalPages && totalItems > 0) {
        totalPages = Math.max(1, Math.ceil(totalItems / perPage));
    }

    if (!totalPages) {
        totalPages = 1;
    }

    return {
        total_items: totalItems,
        current_page: currentPage,
        total_pages: totalPages,
    };
}

export function filterSummary(featureFilter = {}) {
  const groups = validGroups(featureFilter.groups || []);

  if (!groups.length) {
    return null;
  }

  if (groups.length > 1) {
    const n = groups.length;
    return n + " filter row" + (n === 1 ? "" : "s") + " applied";
  }

  const n = groups[0].features.length;
  return n + " filter" + (n === 1 ? "" : "s") + " applied";
}

// Aliases used by route resolve / older imports
export const parseFeatureFilterFromParams = parseFilterFromParams;
export const createEmptyFilterGroup = emptyGroup;
export const getInitialEditorGroups = editorGroups;
export const getAppliedFilterKey = filterKey;
export const getValidFilterGroups = validGroups;
export const getActiveFilterSummary = filterSummary;
export const FEATURE_ENABLED_VALUES = ENABLED;
export const FEATURE_MATCH_VALUES = MATCH;
